import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const brand = useBrand();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      const map = { student: '/student', teacher: '/teacher', developer: '/developer' };
      navigate(map[user.role] || '/student');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left brand panel */}
      <div className="hidden md:flex md:w-1/2 flex-col justify-between p-12 text-white"
           style={{ background: 'var(--ai-hero-gradient)' }}>
        <div className="flex items-center gap-4 self-start">
          <img
            src="/branding/ai-tutor-hero.png"
            alt={brand.product_name}
            className="h-24 w-24 rounded-xl object-contain ring-1 ring-white/15"
          />
          <div className="leading-tight">
            <div className="text-3xl font-extrabold tracking-tight" style={{ fontFamily: 'Georgia, "Times New Roman", serif', color: 'var(--gr-bone)' }}>
              IoA
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] mt-1" style={{ color: 'var(--gr-gold)' }}>
              Institute of Analytics
            </div>
          </div>
        </div>

        {/* AI TUTOR — INSTITUTE OF ANALYTICS sits centered in the empty
            band between the logo above and the module-scope/headline block
            below. We achieve this by making it its own flex child so that
            `justify-between` on the parent distributes equal vertical gaps:
            logo ⇄ tagline ⇄ scope+headline ⇄ copyright. */}
        <div
          className="text-2xl uppercase tracking-widest opacity-80"
          data-testid="login-brand-tagline"
        >
          {brand.course_title} — {brand.institution_short_name}
        </div>

        <div>
          <h2 className="text-4xl font-bold leading-tight mb-10" data-testid="login-course-headline">{brand.course_subtitle}</h2>
          {brand.course_description ? (
            <p
              className="text-2xl max-w-md leading-snug"
              style={{ color: 'var(--gr-gold)', fontWeight: 800 }}
              data-testid="login-course-scope"
            >
              {brand.course_description}
            </p>
          ) : null}
        </div>
        <div className="text-xs opacity-75">© {brand.parent_org_name}</div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md">
          <h1 className="text-3xl font-bold mb-1" style={{ color: 'var(--unthsc-green)' }}>Welcome back</h1>
          <p className="text-sm text-gray-500 mb-8">Sign in to continue to your dashboard.</p>

          <form onSubmit={handleSubmit} data-testid="login-form">
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-red-700 text-sm" data-testid="login-error">
                {error}
              </div>
            )}

            <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="input-field mb-4"
              required
              data-testid="login-email-input"
            />

            <label className="block text-sm font-semibold text-gray-700 mb-1">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="input-field mb-6"
              required
              data-testid="login-password-input"
            />

            <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="login-submit-btn">
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>

          <p className="mt-6 text-xs text-gray-500 text-center">
            Need an account? Contact your administrator for an invitation link.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
